import {
  Header,
  Page,
  Content,
  ItemCardGrid,
  Progress,
  ResponseErrorPanel,
} from '@backstage/core-components';
import { useApi } from '@backstage/core-plugin-api';
import {
  catalogApiRef,
  entityRouteParams,
} from '@backstage/plugin-catalog-react';
import { AcceleratorCard, AcceleratorCardProps } from '.';
import useAsync from 'react-use/esm/useAsync';
import { Entity, stringifyEntityRef } from '@backstage/catalog-model';
import { Typography, Box } from '@material-ui/core';

const CATEGORIES = ['AI', 'Infrastructure', 'Frontend'];

export const AcceleratorPage = () => {
  const catalogApi = useApi(catalogApiRef);

  const {
    value: templates,
    loading,
    error,
  } = useAsync(async () => {
    const { items } = await catalogApi.getEntities({
      filter: { kind: 'Template' },
      fields: [
        'kind',
        'metadata.namespace',
        'metadata.name',
        'metadata.title',
        'metadata.description',
        'metadata.category',
        'spec.type',
        'metadata.tags',
      ],
    });

    return items.filter((e): e is Entity => !!e && e.kind === 'Template');
  }, [catalogApi]);

  // Map templates to AcceleratorCardProps and group by category
  const cardsByCategory: Record<string, AcceleratorCardProps[]> = {};

  templates?.forEach(template => {
    const params = entityRouteParams(template);
    const category = (template.metadata as any)?.category || 'Frontend';

    const card: AcceleratorCardProps = {
      title: template.metadata.title || template.metadata.name,
      description: template.metadata.description || '',
      type: (template.spec as any)?.type,
      tags: template.metadata.tags,
      detailsHref: `/catalog/${params.namespace}/${params.kind}/${params.name}`,
      launchHref: `/create/templates/${params.namespace}/${params.name}`,
      entityRef: stringifyEntityRef(template),
    };

    if (!cardsByCategory[category]) {
      cardsByCategory[category] = [];
    }
    cardsByCategory[category].push(card);
  });

  return (
    <Page themeId="home">
      <Header
        title="Accelerators"
        subtitle="Jumpstart your next project with curated templates"
      />
      <Content>
        {loading && <Progress />}
        {error && <ResponseErrorPanel error={error} />}
        {!loading && !error && (
          <>
            {CATEGORIES.map(category => {
              const categoryCards = cardsByCategory[category] || [];
              return (
                <Box key={category} mb={4}>
                  <Typography variant="h5" gutterBottom>
                    {category}
                  </Typography>
                  <ItemCardGrid>
                    {categoryCards.map(accelerator => (
                      <AcceleratorCard
                        key={accelerator.detailsHref}
                        {...accelerator}
                      />
                    ))}
                  </ItemCardGrid>
                </Box>
              );
            })}
          </>
        )}
      </Content>
    </Page>
  );
};
